import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { LucideAngularModule } from 'lucide-angular';
import { NgxPaginationModule } from 'ngx-pagination';
import { IntroductionComponent } from '../../components/introduction/introduction.component';
import { PopupMessageComponent } from '../../components/popup-message/popup-message.component';
import { SpinnerComponent } from '../../components/spinner/spinner.component';
import { PrvNextButtonsComponent } from '../../components/prv-next-buttons/prv-next-buttons.component';
import { DeleteConfirmationComponent } from '../../components/delete-confirmation/delete-confirmation.component';
import { Icons } from '../../../shared/helpers/icons';
import { IUserInfo } from '../../../shared/models/contact';
import { UserDataService } from '../../../shared/services/userData.service';
import { IEmergencyEvacuationKit } from '../../../shared/models/emergencyEvacuationKit';
import { EmergencyEvacuationKitsService } from '../../../shared/services/emergencyEvacuationKits.service';
import { EmergencyEvacuationKitFormComponent } from './emergency-evacuation-kit-form/emergency-evacuation-kit-form.component';

@Component({
  selector: 'emergency-evacuation-kits',
  standalone: true,
  imports: [
    CommonModule,
    LucideAngularModule,
    NgxPaginationModule,
    IntroductionComponent,
    PopupMessageComponent,
    SpinnerComponent,
    PrvNextButtonsComponent,
    DeleteConfirmationComponent,
    EmergencyEvacuationKitFormComponent
  ],
  templateUrl: './emergency-evacuation-kits.component.html',
  styleUrl: './emergency-evacuation-kits.component.css'
})
export class EmergencyEvacuationKitsComponent implements OnInit {

  icons = Icons;
  currentBuildingId: string | null = null;
  currentUser: IUserInfo | null = null;

  kitsList: IEmergencyEvacuationKit[] = [];
  selectedItem: IEmergencyEvacuationKit | null = null;

  showForm: boolean = false;
  formItem: IEmergencyEvacuationKit | null = null;   // null = add, otherwise edit
  showDeleteConfirmation: boolean = false;

  loading: boolean = false;
  messageModal: string | null = null;
  popupMessage: string = '';

  // Table sort + pagination
  sortOrder: boolean = true;
  page: number = 1;
  itemsPerPage: number = 10;

  constructor(
    private userDataService: UserDataService,
    private emergencyEvacuationKitsService: EmergencyEvacuationKitsService
  ) {}

  ngOnInit(): void {
    this.currentBuildingId = this.userDataService.getBuildingId();
    this.currentUser = this.userDataService.getUserData();
    this.loadKits();
  }

  loadKits(): void {
    if (!this.currentBuildingId) return;
    this.loading = true;
    this.emergencyEvacuationKitsService.getEmergencyEvacuationKits(this.currentBuildingId).subscribe({
      next: (response) => {
        this.loading = false;
        this.kitsList = response && response.data ? response.data : [];
      },
      error: (error: any) => {
        this.loading = false;
        this.messageModal = 'unsuccess';
        this.popupMessage = error.message;
      }
    });
  }

  sortList(key: keyof IEmergencyEvacuationKit): void {
    this.sortOrder = !this.sortOrder;
    this.kitsList.sort((a, b) => {
      const valueA = a[key]?.toString().toLowerCase() || '';
      const valueB = b[key]?.toString().toLowerCase() || '';
      if (valueA < valueB) return this.sortOrder ? -1 : 1;
      if (valueA > valueB) return this.sortOrder ? 1 : -1;
      return 0;
    });
  }

  openAdd(): void {
    this.formItem = null;
    this.showForm = true;
  }

  openEdit(item: IEmergencyEvacuationKit): void {
    this.formItem = item;
    this.showForm = true;
  }

  closeForm(): void {
    this.showForm = false;
    this.formItem = null;
  }

  handleSaved(message: { type: string, message: string }): void {
    this.closeForm();
    this.loadKits();
    this.messageModal = message.type;
    this.popupMessage = message.message;
  }

  openDelete(item: IEmergencyEvacuationKit): void {
    this.selectedItem = item;
    this.showDeleteConfirmation = true;
  }

  closeDelete(): void {
    this.showDeleteConfirmation = false;
    this.selectedItem = null;
  }

  handleDelete(item: IEmergencyEvacuationKit): void {
    this.showDeleteConfirmation = false;
    if (!this.currentBuildingId || !item?.id) return;
    this.loading = true;
    this.emergencyEvacuationKitsService.deleteEmergencyEvacuationKit(this.currentBuildingId, String(item.id)).subscribe({
      next: () => {
        this.selectedItem = null;
        this.loading = false;
        this.messageModal = 'success';
        this.popupMessage = 'This occurred after deleting an evacuation kit.';
        this.loadKits();
      },
      error: (error: any) => {
        this.selectedItem = null;
        this.loading = false;
        this.messageModal = 'unsuccess';
        this.popupMessage = error.message;
      }
    });
  }

  canSave(): boolean {
    if (!this.currentUser) return false;
    return this.currentUser.can_edit || this.currentUser.can_admin;
  }

  resetMessageModal(): void {
    this.messageModal = null;
    this.popupMessage = '';
  }
}
